/**
 * HmContractorForm — zakładanie i edycja kontrahenta w Symfonii. Jeden formularz, dwa tryby.
 *
 * DLACZEGO JEDEN KOMPONENT: pola są identyczne, różni się tylko to, skąd bierze się stan
 * początkowy i dokąd leci zapis. Dwa bliźniacze ekrany rozjechałyby się przy pierwszej zmianie.
 *
 * KOD KONTRAHENTA jest edytowalny WYŁĄCZNIE przy zakładaniu. W Symfonii zmiana kodu nie jest
 * edycją — przy towarach `Update` z nieznanym kodem ZAKŁADA nowy wpis, więc przy edycji pole
 * jest zablokowane i nie trafia do żądania.
 *
 * ZAPIS JEST NIEODWRACALNY z naszej strony: WebAPI nie usuwa kontrahentów, da się ich najwyżej
 * dezaktywować. Stąd potwierdzenie przed założeniem — jak przy dokumentach kasowych.
 *
 * PESEL nie występuje w tym formularzu i nie jest wysyłany. Backend chroni go osobno: edycja
 * nanosi zmiany na świeżo odczytaną kartę, więc pole, którego nie znamy, przechodzi nietknięte.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { HmContractorDetail } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Save } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DetailError, Section } from '@/components/handel-magazyn/detail-parts';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

interface FormState {
  code: string;
  name: string;
  taxId: string;
  regon: string;
  active: boolean;
  vatTaxPayer: boolean;
  vies: boolean;
  note: string;
  contactName: string;
  contactSurname: string;
  phone1: string;
  email: string;
  city: string;
  postCode: string;
  street: string;
  houseNo: string;
  accountNumber: string;
  bankName: string;
}

const EMPTY: FormState = {
  code: '',
  name: '',
  taxId: '',
  regon: '',
  active: true,
  vatTaxPayer: false,
  vies: false,
  note: '',
  contactName: '',
  contactSurname: '',
  phone1: '',
  email: '',
  city: '',
  postCode: '',
  street: '',
  houseNo: '',
  accountNumber: '',
  bankName: '',
};

function fromDetail(c: HmContractorDetail): FormState {
  const [first = '', ...rest] = (c.contact.name ?? '').split(' ');
  return {
    code: c.code,
    name: c.name,
    taxId: c.taxId ?? '',
    regon: c.regon ?? '',
    active: c.active,
    vatTaxPayer: c.vatTaxPayer,
    vies: c.vies,
    note: c.note ?? '',
    contactName: first,
    contactSurname: rest.join(' '),
    phone1: c.contact.phone1 ?? '',
    email: c.contact.email ?? '',
    city: c.defaultAddress?.city ?? '',
    postCode: c.defaultAddress?.postCode ?? '',
    street: c.defaultAddress?.street ?? '',
    houseNo: c.defaultAddress?.houseNo ?? '',
    accountNumber: c.bank.accountNumber ?? '',
    bankName: c.bank.bankName ?? '',
  };
}

/** Formularz → ciało żądania. Kod leci TYLKO przy zakładaniu (patrz nagłówek). */
function toPayload(form: FormState, mode: 'create' | 'edit') {
  return {
    ...(mode === 'create' ? { code: form.code.trim() } : {}),
    name: form.name.trim(),
    taxId: form.taxId.trim(),
    regon: form.regon.trim(),
    active: form.active,
    vatTaxPayer: form.vatTaxPayer,
    vies: form.vies,
    note: form.note.trim(),
    contact: {
      name: form.contactName.trim(),
      surname: form.contactSurname.trim(),
      phone1: form.phone1.trim(),
      email: form.email.trim(),
    },
    defaultAddress: {
      city: form.city.trim(),
      postCode: form.postCode.trim(),
      street: form.street.trim(),
      houseNo: form.houseNo.trim(),
    },
    bank: { accountNumber: form.accountNumber.trim(), bankName: form.bankName.trim() },
  };
}

export function HmContractorForm({ mode }: { mode: 'create' | 'edit' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const companyId = useActiveCompanyScope().companyId;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const existing = useQuery({
    queryKey: ['handel-magazyn', 'contractor', id, companyId],
    enabled: mode === 'edit' && !!id && !!companyId,
    retry: false,
    queryFn: async (): Promise<HmContractorDetail> =>
      (
        await api.get<HmContractorDetail>(
          `/handel-magazyn/contractors/${id}?companyId=${companyId}`,
        )
      ).data,
  });

  useEffect(() => {
    if (existing.data) setForm(fromDetail(existing.data));
  }, [existing.data]);

  const mutation = useMutation({
    mutationFn: async (): Promise<HmContractorDetail> => {
      const payload = toPayload(form, mode);
      const url =
        mode === 'create'
          ? `/handel-magazyn/contractors?companyId=${companyId}`
          : `/handel-magazyn/contractors/${id}?companyId=${companyId}`;
      const res =
        mode === 'create'
          ? await api.post<HmContractorDetail>(url, payload)
          : await api.put<HmContractorDetail>(url, payload);
      return res.data;
    },
    onSuccess: (contractor) => {
      toast.show({
        variant: 'success',
        title:
          mode === 'create'
            ? t('tenant.handelMagazyn.contractors.toast.created')
            : t('tenant.handelMagazyn.contractors.toast.updated'),
      });
      void queryClient.invalidateQueries({ queryKey: ['handel-magazyn'] });
      navigate(`/handel-magazyn/kontrahenci/${contractor.id}`);
    },
    onError: (err) => {
      // Odmowa Symfonii niesie POWÓD (np. duplikat kodu) — pokazujemy jej treść, bo tylko ona
      // mówi, co poprawić. Nasz własny komunikat wysłałby użytkownika szukać nie tam.
      const refusal = isAxiosError(err)
        ? (err.response?.data as { error?: string; details?: string } | undefined)
        : undefined;
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.contractors.toast.failed'),
        description: refusal?.error === 'SYMFONIA_REFUSED' ? refusal.details : undefined,
      });
    },
  });

  if (!companyId) {
    return (
      <div className="p-4 text-sm text-text-secondary">{t('tenant.handelMagazyn.pickCompany')}</div>
    );
  }
  if (mode === 'edit' && existing.isLoading) {
    return <div className="p-4 text-sm text-text-secondary">{t('common.loading')}</div>;
  }
  if (mode === 'edit' && existing.error) return <DetailError error={existing.error} />;

  const canSubmit =
    form.name.trim() !== '' && (mode === 'edit' || form.code.trim() !== '') && !mutation.isPending;

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">
            {mode === 'create'
              ? t('tenant.handelMagazyn.contractors.newTitle')
              : t('tenant.handelMagazyn.contractors.editTitle')}
          </h1>
          <p className="text-sm text-text-secondary">
            {t('tenant.handelMagazyn.contractors.writeWarning')}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Save className="h-4 w-4" />}
          disabled={!canSubmit}
          loading={mutation.isPending}
          onClick={() => setConfirmOpen(true)}
          data-testid="hm-contractor-save"
        >
          {t('common.save')}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 rounded-md border border-border-primary bg-bg-secondary p-4 lg:grid-cols-2">
        <Section title={t('tenant.handelMagazyn.contractors.identity')}>
          <Input
            label={t('tenant.handelMagazyn.contractors.columns.code')}
            value={form.code}
            // Przy edycji kod jest ZABLOKOWANY — jego zmiana w Symfonii nie jest edycją.
            disabled={mode === 'edit'}
            hint={mode === 'edit' ? t('tenant.handelMagazyn.contractors.codeLocked') : undefined}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            data-testid="hm-contractor-code"
          />
          <Input
            label={t('tenant.handelMagazyn.contractors.columns.name')}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="hm-contractor-name"
          />
          <Input
            label={t('tenant.handelMagazyn.contractors.columns.taxId')}
            value={form.taxId}
            onChange={(e) => setForm((f) => ({ ...f, taxId: e.target.value }))}
          />
          <Input
            label={t('tenant.handelMagazyn.contractors.regon')}
            value={form.regon}
            onChange={(e) => setForm((f) => ({ ...f, regon: e.target.value }))}
          />
          <div className="mt-2 space-y-1.5">
            <Checkbox
              label={t('tenant.handelMagazyn.contractors.active')}
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
            />
            <Checkbox
              label={t('tenant.handelMagazyn.contractors.vatTaxPayer')}
              checked={form.vatTaxPayer}
              onChange={(e) => setForm((f) => ({ ...f, vatTaxPayer: e.target.checked }))}
            />
            <Checkbox
              label={t('tenant.handelMagazyn.contractors.vies')}
              checked={form.vies}
              onChange={(e) => setForm((f) => ({ ...f, vies: e.target.checked }))}
            />
          </div>
        </Section>

        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.contractors.defaultAddress')}>
            <Input
              label={t('tenant.handelMagazyn.contractors.columns.city')}
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            />
            <Input
              label={t('tenant.handelMagazyn.contractors.columns.postCode')}
              value={form.postCode}
              onChange={(e) => setForm((f) => ({ ...f, postCode: e.target.value }))}
            />
            <Input
              label={t('tenant.handelMagazyn.contractors.street')}
              value={form.street}
              onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
            />
            <Input
              label={t('tenant.handelMagazyn.contractors.houseNo')}
              value={form.houseNo}
              onChange={(e) => setForm((f) => ({ ...f, houseNo: e.target.value }))}
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.contractors.contact')}>
            <Input
              label={t('tenant.handelMagazyn.detail.name')}
              value={form.contactName}
              onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
            />
            <Input
              label={t('tenant.handelMagazyn.contractors.surname')}
              value={form.contactSurname}
              onChange={(e) => setForm((f) => ({ ...f, contactSurname: e.target.value }))}
            />
            <Input
              label={t('tenant.handelMagazyn.contractors.phone')}
              value={form.phone1}
              onChange={(e) => setForm((f) => ({ ...f, phone1: e.target.value }))}
            />
            <Input
              label={t('tenant.handelMagazyn.contractors.email')}
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.contractors.bank')}>
            <Input
              label={t('tenant.handelMagazyn.contractors.accountNumber')}
              value={form.accountNumber}
              onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
            />
            <Input
              label={t('tenant.handelMagazyn.contractors.bankName')}
              value={form.bankName}
              onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
            />
          </Section>
        </div>
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-sm text-text-secondary">
          {t('tenant.handelMagazyn.contractors.note')}
        </label>
        <Textarea
          value={form.note}
          rows={3}
          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
        />
      </div>

      <ConfirmDialog
        open={confirmOpen}
        variant="primary"
        title={
          mode === 'create'
            ? t('tenant.handelMagazyn.contractors.confirmCreate')
            : t('tenant.handelMagazyn.contractors.confirmUpdate')
        }
        message={t('tenant.handelMagazyn.contractors.writeWarning')}
        confirmLabel={t('common.save')}
        onConfirm={() => {
          setConfirmOpen(false);
          mutation.mutate();
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}

export default HmContractorForm;
